import type { PaymentPlanKind, TransactionsListItem } from '../api/response-types'
import { isTerminal } from '../api/types'

export interface PlanPresentation {
  kindLabel: string
  showSchedule: boolean
  showScheduleOperations: boolean
  showSuspendAction: boolean
  showResumeAction: boolean
  showCompleteAction: boolean
  showReopenAction: boolean
  showRefundRecollection: boolean
  suspendedPlanWarning: string | null
  terminationAmount: 'outstanding' | 'settled'
  terminationAmountLabel: string
  terminationWarning: string
  terminationButtonIncludesAmount: boolean
  eventTabs: Array<{ id: 'completed' | 'upcoming'; label: string }>
  dashboard: {
    checkoutAmountLabel: string | null
    checkoutDateLabel: string | null
    nextInstalmentPlaceholder: string | null
  }
  paymentTabs: Array<{ id: 'instalments' | 'transactions'; label: string }>
}

export function formatPlanKind(kind: PaymentPlanKind): string {
  return kind === 'in-full' ? 'Paid in full' : 'Instalments'
}

export function presentStatus(
  kind: PaymentPlanKind,
  status: string,
  transactions: ReadonlyArray<Pick<TransactionsListItem, 'status'>> = [],
): string {
  if (isTerminal(status) && isFullyRefunded(transactions)) {
    return 'refunded'
  }
  return kind === 'in-full' && status === 'active' ? 'Awaiting ticketing' : status
}

function isFullyRefunded(transactions: ReadonlyArray<Pick<TransactionsListItem, 'status'>>): boolean {
  const settled = transactions.filter(x => x.status !== 'failed')
  return settled.length > 0 && settled.every(x => x.status === 'refunded')
}

export function planPresentation(kind: PaymentPlanKind): PlanPresentation {
  if (kind === 'in-full') {
    return {
      kindLabel: formatPlanKind(kind),
      showSchedule: false,
      showScheduleOperations: false,
      showSuspendAction: false,
      showResumeAction: false,
      showCompleteAction: true,
      showReopenAction: false,
      showRefundRecollection: false,
      suspendedPlanWarning:
        "Ticketing can't complete this plan, and it can't be resumed. Refund first, then terminate.",
      terminationAmount: 'settled',
      terminationAmountLabel: 'Charged at checkout:',
      terminationWarning:
        'Refund first where you can. The payment can still be refunded from this page after the plan is closed. Terminating also stops the automatic e-ticket handover.',
      terminationButtonIncludesAmount: false,
      eventTabs: [{ id: 'completed', label: 'Completed' }],
      dashboard: {
        checkoutAmountLabel: 'Charged',
        checkoutDateLabel: 'Charged on',
        nextInstalmentPlaceholder: '—',
      },
      paymentTabs: [{ id: 'transactions', label: 'Transactions' }],
    }
  }

  return {
    kindLabel: formatPlanKind(kind),
    showSchedule: true,
    showScheduleOperations: true,
    showSuspendAction: true,
    showResumeAction: true,
    showCompleteAction: false,
    showReopenAction: true,
    showRefundRecollection: true,
    suspendedPlanWarning: null,
    terminationAmount: 'outstanding',
    terminationAmountLabel: 'Outstanding amount:',
    terminationWarning:
      'This removes any outstanding payments. The customer cannot be charged or refunded anymore. If a refund is outstanding, suspend the plan instead.',
    terminationButtonIncludesAmount: true,
    eventTabs: [
      { id: 'completed', label: 'Completed' },
      { id: 'upcoming', label: 'Upcoming' },
    ],
    dashboard: {
      checkoutAmountLabel: null,
      checkoutDateLabel: null,
      nextInstalmentPlaceholder: null,
    },
    paymentTabs: [
      { id: 'instalments', label: 'Instalments' },
      { id: 'transactions', label: 'Transactions' },
    ],
  }
}
